import type { UUID } from '@/types/Id'

// --- Types matching backend slim models ---

export interface SalesforceNamedEntity {
  Id: string
  Name: string
}

export interface SalesforceAttributes {
  type: string
  url: string
}

export interface SalesforceOpportunityCreate {
  Name: string
  CloseDate: string
  StageName: string
}

export interface SalesforceOpportunity {
  Id: string
  Name: string
  AccountId?: string
  Account?: SalesforceNamedEntity
  Amount?: number
  Description?: string
  CloseDate: string // ISO date string
  CreatedById?: string
  CreatedBy?: SalesforceNamedEntity
  OwnerId?: string
  Owner?: SalesforceNamedEntity
  StageName: string
  Probability?: number
  Type?: string
  LastModifiedDate: string // ISO datetime string
  Attributes?: SalesforceAttributes
}

export interface SalesforceOpportunityStage {
  active: boolean
  defaultValue: boolean
  label: string
  value: string
}

export interface SalesforceStageMapping {
  id: UUID
  created_at: string // ISO datetime string
  last_modified: string // ISO datetime string
  organization_id: UUID
  pursuit_stage_id: UUID
  salesforce_stage_name: string
  auto_sync: boolean
}

export interface SalesforceRecordType {
  Id: string
  Name: string
  DeveloperName: string
  IsActive: boolean
  Description?: string
  BusinessProcessId?: string
}

export type SalesforceAdditionalFields = Record<
  string,
  string | number | boolean | null
>

export interface SalesforceOpportunityFromPursuitCreate {
  pursuit_id: UUID
  additional_fields: SalesforceAdditionalFields
}

export interface SalesforceOpportunityPushRequest {
  pursuits: SalesforceOpportunityFromPursuitCreate[]
}

export type SalesforceFieldType =
  | 'string'
  | 'multipicklist'
  | 'picklist'
  | 'reference'
  | 'boolean'
  | 'date'
  | 'datetime'
  | 'int'
  | 'double'
  | 'percent'
  | 'currency'
  | 'textarea'
  | 'phone'
  | 'email'
  | 'url'
  | 'id'

export interface SalesforceFieldPicklistValue {
  active: boolean
  defaultValue: boolean
  label: string
  value: string
}

export interface SalesforceField {
  name: string
  label: string
  type: SalesforceFieldType
  nillable: boolean
  createable: boolean
  defaultedOnCreate: boolean
  defaultValue: string | number | boolean | null
  controllerName?: string | null
  picklistValues?: SalesforceFieldPicklistValue[] | null
  referenceTo?: string[] | null
  relationshipName?: string | null
}

export interface ChildField {
  childSObject: string
  field: string
  relationshipName?: string | null
}

export interface ValidationRule {
  id: string
  name: string
  error_condition_formula: string
  error_message: string
}

export interface SalesforceFields {
  fields: SalesforceField[]
  childRelationships: ChildField[]
  validationRules: ValidationRule[]
}

// --- Query key helpers ---

interface SalesforceIntegrationQueryKeyArgs {
  orgId: UUID | ''
}

export const salesforceIntegrationQueryKeys = {
  all: () => ['salesforce_integration'] as const,
  organization: (keyArgs: SalesforceIntegrationQueryKeyArgs) =>
    ['salesforce_integration', keyArgs.orgId] as const,
}

interface SalesforceOpportunityStagesQueryKeyArgs {
  orgId: UUID | ''
}

export const salesforceOpportunityStagesQueryKeys = {
  all: () => ['salesforce_opportunity_stages'] as const,
  organization: (keyArgs: SalesforceOpportunityStagesQueryKeyArgs) =>
    ['salesforce_opportunity_stages', keyArgs.orgId] as const,
}

interface SalesforceStageMappingsQueryKeyArgs {
  orgId: UUID | ''
}

export const salesforceStageMappingsQueryKeys = {
  all: () => ['salesforce_stage_mappings'] as const,
  organization: (keyArgs: SalesforceStageMappingsQueryKeyArgs) =>
    ['salesforce_stage_mappings', keyArgs.orgId] as const,
}

interface SalesforcePushStatusQueryKeyArgs {
  orgId: UUID | ''
}

export const salesforcePushStatusQueryKeys = {
  all: () => ['salesforce_push_status'] as const,
  organization: (keyArgs: SalesforcePushStatusQueryKeyArgs) =>
    ['salesforce_push_status', keyArgs.orgId] as const,
}

export interface SalesforceOpportunityFieldsQueryKeyArgs {
  orgId: UUID | ''
}

export const salesforceOpportunityFieldsQueryKeys = {
  all: () => ['salesforce_opportunity_fields'] as const,
  organization: (keyArgs: SalesforceOpportunityFieldsQueryKeyArgs) =>
    ['salesforce_opportunity_fields', keyArgs.orgId] as const,
}
